import * as React from 'react'
import {useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, PermissionObject} from 'yougile-model'
import {Sync} from '@client/sync'
import {LinkButtonNew} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {getIcon} from '@components/permissions/permissions-settings/permissions-settings-utils'
import {useTooltipVisible} from '@containers/hooks'
import {PermissionsBoardSettings} from './permissions-board-settings'
import {PermissionsColumnSettings} from './permissions-column-settings'
import {PermissionsProjectSettings} from './permissions-project-settings'
import {PermissionsSettingsDisallowed} from './permissions-settings-disallowed'
import {PermissionsTaskSettings} from './permissions-task-settings'

const TwTitle = tw.div`
  flex
  gap-4
  px-16
  pt-16
  pb-8
`

const TwText = tw.div`
  flex-auto
  text-sm-semibold
  text-primary
  whitespace-nowrap
  overflow-hidden
  text-ellipsis
`

export const TwTooltipText = tw.span`
  break-words
  line-clamp-7
`

interface Props {
  object: PermissionObject
  isAvailable: (id: DataObjectId) => boolean
  onChange: (obj: PermissionObject) => void
  revert: () => void
  isNew?: boolean
  isChanged?: boolean
}

export const PermissionsSettings = (props: Props) => {
  const {object, isAvailable, isNew, isChanged, revert} = props
  const canEdit = isNew || isAvailable(object.objectId)

  const box = Sync.getCurrentSandbox()
  const hub = box.getHub(object.objectId)

  let content = null
  if (!canEdit) {
    content = <PermissionsSettingsDisallowed />
  } else if (object?.type === 'project') {
    content = <PermissionsProjectSettings {...props} />
  } else if (object?.type === 'board') {
    content = <PermissionsBoardSettings {...props} />
  } else if (object?.type === 'column') {
    content = <PermissionsColumnSettings {...props} />
  } else if (object?.type === 'task') {
    content = <PermissionsTaskSettings {...props} allowEmpty={false} />
  }

  const titleRef = useRef<HTMLElement>(null)
  const tooltipVisible = useTooltipVisible(titleRef, [hub?.title])

  return (
    <div className="pb-16">
      {hub && (
        <TwTitle>
          <Icon icon={getIcon(object.type, hub)} color="icon-secondary" />
          <Tooltip
            content={<TwTooltipText>{hub.title}</TwTooltipText>}
            placement="bottom"
            disabled={!tooltipVisible}
            offset={[0, 8]}
          >
            <TwText ref={titleRef}>{hub.title}</TwText>
          </Tooltip>
          {isChanged && (
            <LinkButtonNew
              title={Lang.msg.permission.revert}
              className="ml-auto"
              onClick={revert}
            />
          )}
        </TwTitle>
      )}
      {content}
    </div>
  )
}
